import { createI18n } from 'vue-i18n';
import en from './en';
import fr from './fr';
import es from './es';
import pt from './pt';

const LOCALE_STORAGE_KEY = 'overpassPlanner.locale';
const SUPPORTED_LOCALES = ['en', 'fr', 'es', 'pt'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Detect the best locale from the browser's language preferences.
 *
 * `navigator.languages` reflects the user's OS / browser language settings,
 * which naturally handles sub-national regions:
 *   - Quebec (Canada) → fr-CA → fr
 *   - Brazil → pt-BR → pt
 *   - Mexico → es-MX → es
 *   - Switzerland (French) → fr-CH → fr
 *   - French Guiana → fr-GF → fr
 *   - Angola → pt-AO → pt
 *   - etc.
 *
 * This is more reliable than coordinate-based detection because it reflects
 * the user's actual language preference, not just their physical location.
 */
function detectBrowserLocale(): SupportedLocale {
  if (typeof window === 'undefined' || !navigator.languages) return 'en';

  for (const tag of navigator.languages) {
    const primary = tag.split('-')[0].toLowerCase();
    if (SUPPORTED_LOCALES.includes(primary as SupportedLocale)) {
      return primary as SupportedLocale;
    }
  }
  return 'en';
}

function getInitialLocale(): SupportedLocale {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
      return saved as SupportedLocale;
    }
  } catch {
    // Ignore storage errors
  }
  // No saved preference — auto-detect from browser languages
  const detected = detectBrowserLocale();
  // Persist so subsequent visits don't re-detect (user can override in Settings)
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, detected);
  } catch {
    // Ignore
  }
  return detected;
}

const initialLocale = getInitialLocale();

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'en',
  messages: { en, fr, es, pt },
});

// Set the html lang attribute on init
if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
}

export function setLocale(locale: string) {
  if (!SUPPORTED_LOCALES.includes(locale as SupportedLocale)) return;
  (i18n.global.locale as unknown as { value: string }).value = locale;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage errors
  }
  document.documentElement.lang = locale;
}

export default i18n;
